/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { DocumentContact } from 'common/types';
import { DOCUMENT_TRADE_SUB_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { isOccupancyLocked } from 'admin/utils/contact-utils/contact-utils';

// Shared case reducers; each slice supplies its own newDraftContact factory.

export interface ContactDraftState {
    draftContact: DocumentContact;
}

export interface ContactListState extends ContactDraftState {
    contacts: DocumentContact[];
}

export interface ContactOccupancyState extends ContactDraftState {
    occupied: boolean;
}

/** Locally created contacts carry a uuid and no id, persisted ones carry an id but no uuid. */
export type ContactIdentity = Pick<DocumentContact, 'uuid' | 'id'>;

export function toContactIdentity(idOrUuid: number | string): ContactIdentity {
    return typeof idOrUuid === 'number' ? { id: idOrUuid } : { uuid: idOrUuid };
}

export function isSameContact(
    contact: DocumentContact,
    identity: ContactIdentity
): boolean {
    if (identity.uuid) {
        return contact.uuid === identity.uuid;
    }
    if (identity.id) {
        return contact.id === identity.id;
    }
    return false;
}

export function createContactDraftReducers<S extends ContactDraftState>(
    newDraftContact: () => DocumentContact
) {
    return {
        setDraftContact: (
            state: S,
            action: PayloadAction<Partial<DocumentContact>>
        ) => {
            state.draftContact = { ...state.draftContact, ...action.payload };
        },

        resetDraftContact: (state: S) => {
            state.draftContact = newDraftContact();
        },
    };
}

export function createContactListReducers<S extends ContactListState>() {
    return {
        addContact: (state: S, action: PayloadAction<DocumentContact>) => {
            const alreadyExists = state.contacts.some(
                contact => contact.uuid === action.payload.uuid
            );
            if (alreadyExists) {
                return;
            }

            state.contacts = [...state.contacts, action.payload];
        },

        removeContact: (state: S, action: PayloadAction<ContactIdentity>) => {
            state.contacts = state.contacts.filter(
                contact => !isSameContact(contact, action.payload)
            );
        },
    };
}

// Occupancy owns the occupant contact type.
export function createContactOccupancyReducers<S extends ContactOccupancyState>(
    occupantTypeId: string
) {
    return {
        setOccupied: (state: S, action: PayloadAction<boolean>) => {
            state.occupied = action.payload;

            if (action.payload) {
                state.draftContact.typeId = occupantTypeId;
            } else if (state.draftContact.typeId === occupantTypeId) {
                state.draftContact.typeId = '';
            }
        },
    };
}

// Occupancy is derived, never stored corrected: a commercial job type can
// never be occupied, whatever the stored flag says.
export function isEffectivelyOccupied(
    occupied: boolean,
    tradeSubTypeId?: DOCUMENT_TRADE_SUB_TYPE
): boolean {
    return occupied && !isOccupancyLocked(tradeSubTypeId);
}
